/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */

import React, { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { Line } from "react-chartjs-2";
import configData from "../../settings_data";
import { ReactGrid } from "@silevis/reactgrid";
export default function Trajectories(_ref) {
  var _parentApi$data$setti;
  let {
    parentApi,
    strategy,
    trajectoriesMeta,
    refTrajectories,
    trajectories,
    setTrajectories
  } = _ref;
  const trajectorySources = JSON.parse((_parentApi$data$setti = parentApi.data.settings) === null || _parentApi$data$setti === void 0 ? void 0 : _parentApi$data$setti.source_suivi_traj);
  const [isOpen, setIsOpen] = useState({});
  const setUserTrajectory = useCallback((trajId, value) => setTrajectories(trajectories => {
    if (value instanceof Function) {
      value = value(trajectories[trajId]);
    }
    const {
      annees_valeurs,
      annees_modifiees
    } = value;
    return {
      ...trajectories,
      [trajId]: {
        annees_valeurs: linearInterpolation(annees_valeurs, annees_modifiees),
        annees_modifiees
      }
    };
  }), [setTrajectories]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, "Comment remplir ma trajectoire ?"), /*#__PURE__*/React.createElement("p", null, "Vous pouvez dessiner des trajectoires :"), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, "en d\xE9pla\xE7ant le point sur le graphique"), /*#__PURE__*/React.createElement("li", null, "ou en saisissant des valeurs ou des \xE9volutions (%) dans le tableau")), (trajectorySources === null || trajectorySources === void 0 ? void 0 : trajectorySources["source"]) && /*#__PURE__*/React.createElement("p", null, trajectorySources["source"]), /*#__PURE__*/React.createElement("p", null, "Source des trajectoires PCAET : Ademe"), /*#__PURE__*/React.createElement("br", null), !(trajectoriesMeta !== null && trajectoriesMeta !== void 0 && trajectoriesMeta.length) ? /*#__PURE__*/React.createElement("p", null, "Chargement en cours...") : trajectoriesMeta.map(meta => /*#__PURE__*/React.createElement("details", {
    key: meta.id,
    className: "tsdetails",
    onToggle: e => setIsOpen(prev => ({
      ...prev,
      [meta.id]: e.target.open
    }))
  }, /*#__PURE__*/React.createElement("summary", null, meta.name, " ", meta.unit && `(${meta.unit})`), strategy !== null && strategy !== void 0 && strategy.referenceYear && isOpen[meta.id] ? /*#__PURE__*/React.createElement(Trajectory, {
    referenceYear: strategy.referenceYear,
    meta: meta,
    refTrajectories: refTrajectories === null || refTrajectories === void 0 ? void 0 : refTrajectories[meta.id],
    userTrajectory: trajectories === null || trajectories === void 0 ? void 0 : trajectories[meta.id],
    setUserTrajectory: setUserTrajectory
  }) : null)));
}
function Trajectory(_ref2) {
  var _refTrajectories$hist2, _refTrajectories$pcae2, _userTrajectory$annee2;
  let {
    referenceYear,
    meta,
    refTrajectories,
    userTrajectory,
    setUserTrajectory
  } = _ref2;
  const years = useMemo(() => {
    const years = [];
    for (let y = referenceYear; y <= configData.planActionDerniereAnnee; y++) {
      years.push(y);
    }
    return years;
  }, [referenceYear]);

  // Hook to initialize the trajectory if needed
  useEffect(() => {
    var _userTrajectory$annee, _refTrajectories$hist, _refTrajectories$pcae;
    const first_year = Math.min(...Object.keys((_userTrajectory$annee = userTrajectory === null || userTrajectory === void 0 ? void 0 : userTrajectory.annees_valeurs) !== null && _userTrajectory$annee !== void 0 ? _userTrajectory$annee : {}));
    if (first_year !== 0 && userTrajectory !== null && userTrajectory !== void 0 && userTrajectory.annees_modifiees && userTrajectory.annees_modifiees[first_year] === false) {
      // Always mark the first trajectory value as modified
      setUserTrajectory(meta.id, {
        annees_valeurs: userTrajectory.annees_valeurs,
        annees_modifiees: {
          ...userTrajectory.annees_modifiees,
          [first_year]: true
        }
      });
    }
    if (userTrajectory !== null && userTrajectory !== void 0 && userTrajectory.annees_valeurs && Object.keys(userTrajectory.annees_valeurs).length || !(refTrajectories !== null && refTrajectories !== void 0 && (_refTrajectories$hist = refTrajectories.historical_data) !== null && _refTrajectories$hist !== void 0 && _refTrajectories$hist.length)) return;
    const referenceValue = refTrajectories.historical_data.map(_ref3 => {
      var _data$find$valeur, _data$find;
      let {
        data
      } = _ref3;
      return (_data$find$valeur = (_data$find = data.find(_ref4 => {
        let {
          annee
        } = _ref4;
        return annee === years[0];
      })) === null || _data$find === void 0 ? void 0 : _data$find.valeur) !== null && _data$find$valeur !== void 0 ? _data$find$valeur : 0;
    }).reduce((a, b) => a + b, 0);

    // If there is no existing trajectory, use the reference value
    const values = {
      [years[0]]: referenceValue
    };
    const modified = Object.fromEntries(years.map((year, index) => ["" + year, index === 0]));
    if (refTrajectories !== null && refTrajectories !== void 0 && (_refTrajectories$pcae = refTrajectories.pcaet_trajectory) !== null && _refTrajectories$pcae !== void 0 && _refTrajectories$pcae.length) {
      // If there is a PCAET, use it as the default trajectory
      const yearsList = refTrajectories.pcaet_trajectory[0].data.map(_ref5 => {
        let {
          annee
        } = _ref5;
        return annee;
      });
      let lastValue = referenceValue;
      for (const year of yearsList) {
        if (year <= years[0]) continue;
        // Sum all categories corresponding to current year to get total value
        const pcaetValue = refTrajectories.pcaet_trajectory.map(_ref6 => {
          var _data$find$valeur2, _data$find2;
          let {
            data
          } = _ref6;
          return (_data$find$valeur2 = (_data$find2 = data.find(_ref7 => {
            let {
              annee
            } = _ref7;
            return annee === year;
          })) === null || _data$find2 === void 0 ? void 0 : _data$find2.valeur) !== null && _data$find$valeur2 !== void 0 ? _data$find$valeur2 : 0;
        }).reduce((a, b) => a + b, 0);
        // Production is always ascending, other indicators are always descending
        const minOrMax = meta.id === "enr_production" ? Math.max : Math.min;
        lastValue = values[year] = minOrMax(lastValue, pcaetValue);
        modified[year] = true;
      }
    }
    setUserTrajectory(meta.id, {
      annees_valeurs: values,
      annees_modifiees: modified
    });
  }, [userTrajectory, refTrajectories, years, setUserTrajectory, meta.id]);

  // TABLE CONFIGURATION
  const tableColumns = [{
    columnId: "header",
    width: 300
  }, ...years.map(year => ({
    columnId: `${year}`,
    width: 60
  }))];
  const tableRows = [{
    rowId: "years",
    cells: [{
      type: "header",
      text: ""
    }, ...years.map(year => ({
      type: "header",
      text: `${year}`
    }))]
  }];
  const numberFormat = Intl.NumberFormat("fr-FR", {
    maximumSignificantDigits: 4
  });
  const percentFormat = Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2
  });
  let referenceValue;
  if (userTrajectory !== null && userTrajectory !== void 0 && userTrajectory.annees_valeurs) {
    const {
      annees_valeurs,
      annees_modifiees
    } = userTrajectory;
    referenceValue = parseFloat(annees_valeurs[referenceYear]);
    tableRows.push({
      rowId: "values",
      cells: [{
        type: "header",
        text: `${meta.name} (${meta.unit})`
      }, ...years.map((year, index) => ({
        type: "text",
        text: numberFormat.format(parseFloat(annees_valeurs[year])),
        className: annees_modifiees[year] ? "" : "cell-placeholder",
        nonEditable: index === 0
      }))]
    });
    tableRows.push({
      rowId: "variations",
      cells: [{
        type: "header",
        text: "Évolution (%)"
      }, ...years.map((year, index) => ({
        type: "text",
        text: percentFormat.format((parseFloat(annees_valeurs[year]) / referenceValue - 1) * 100),
        className: annees_modifiees[year] ? "" : "cell-placeholder",
        nonEditable: index === 0
      }))]
    });
  }
  const handleTableChange = changes => {
    let values = {
      ...userTrajectory.annees_valeurs
    };
    let modified = {
      ...userTrajectory.annees_modifiees
    };
    changes.forEach(_ref8 => {
      let {
        rowId,
        columnId,
        newCell
      } = _ref8;
      if (newCell.text == null || newCell.text === "") {
        modified[columnId] = false;
      } else if (rowId === "values") {
        let value = parseFloat(newCell.text.replace(/\s/, "").replace(",", "."));
        if (isNaN(value)) return;
        if (value < 0) value = 0;
        values[columnId] = value;
        modified[columnId] = true;
      } else if (rowId === "variations") {
        let value = parseFloat(newCell.text.replace(/\s/, "").replace(",", "."));
        if (isNaN(value) || referenceValue === undefined) return;
        value = (value / 100 + 1) * referenceValue;
        values[columnId] = value;
        modified[columnId] = true;
      }
    });
    setUserTrajectory(meta.id, {
      annees_valeurs: values,
      annees_modifiees: modified
    });
  };

  // CHART CONFIGURATION
  const chartData = {
    datasets: []
  };
  const annotations = [];
  if (refTrajectories !== null && refTrajectories !== void 0 && (_refTrajectories$hist2 = refTrajectories.historical_data) !== null && _refTrajectories$hist2 !== void 0 && _refTrajectories$hist2.length) {
    const yearsList = refTrajectories.historical_data[0].data.map(_ref9 => {
      let {
        annee
      } = _ref9;
      return annee;
    });
    const dataSet = yearsList.map(year => ({
      x: new Date(year + "-01-01"),
      // Sum all categories corresponding to current year to get total value
      y: refTrajectories.historical_data.map(_ref10 => {
        var _data$find$valeur3, _data$find3;
        let {
          data
        } = _ref10;
        return (_data$find$valeur3 = (_data$find3 = data.find(_ref11 => {
          let {
            annee
          } = _ref11;
          return annee === year;
        })) === null || _data$find3 === void 0 ? void 0 : _data$find3.valeur) !== null && _data$find$valeur3 !== void 0 ? _data$find$valeur3 : 0;
      }).reduce((a, b) => a + b, 0)
    }));
    chartData.datasets.push({
      borderColor: "#000",
      backgroundColor: "#000",
      pointStyle: "circle",
      label: "Historique",
      pointRadius: 3,
      borderWidth: 2,
      order: 1,
      data: dataSet,
      labels: yearsList
    });
    annotations.push({
      type: "line",
      mode: "vertical",
      scaleID: "x",
      value: new Date(referenceYear + "-01-01"),
      borderColor: "red",
      label: {
        position: "end",
        xAdjust: -50,
        backgroundColor: "rgba(0,0,0,0.7)",
        content: "Historique",
        enabled: true
      }
    });
    annotations.push({
      type: "line",
      mode: "vertical",
      scaleID: "x",
      value: new Date(referenceYear + "-01-01"),
      borderColor: "red",
      label: {
        position: "end",
        xAdjust: 50,
        backgroundColor: "rgba(0,0,0,0.7)",
        content: "Projection",
        enabled: true
      }
    });
  }
  if (userTrajectory !== null && userTrajectory !== void 0 && userTrajectory.annees_valeurs && Object.keys(userTrajectory.annees_valeurs).length) {
    const {
      annees_valeurs,
      annees_modifiees
    } = userTrajectory;
    const dataSet = Object.entries(annees_valeurs).map(_ref12 => {
      let [year, value] = _ref12;
      return {
        x: new Date(year + "-01-01"),
        y: value
      };
    });
    chartData.datasets.push({
      borderColor: "#666",
      backgroundColor: "#666",
      pointStyle: "circle",
      label: "Trajectoire cible",
      pointRadius: Object.values(annees_modifiees).map(m => m ? 5 : 3),
      pointHoverRadius: 7,
      borderWidth: 2,
      order: 0,
      data: dataSet,
      labels: Object.keys(annees_valeurs)
    });
  }
  if (refTrajectories !== null && refTrajectories !== void 0 && (_refTrajectories$pcae2 = refTrajectories.pcaet_trajectory) !== null && _refTrajectories$pcae2 !== void 0 && _refTrajectories$pcae2.length) {
    const yearsList = refTrajectories.pcaet_trajectory[0].data.map(_ref13 => {
      let {
        annee
      } = _ref13;
      return annee;
    });
    const dataSet = yearsList.map(year => ({
      x: new Date(year + "-01-01"),
      // Sum all categories corresponding to current year to get total value
      y: refTrajectories.pcaet_trajectory.map(_ref14 => {
        var _data$find$valeur4, _data$find4;
        let {
          data
        } = _ref14;
        return (_data$find$valeur4 = (_data$find4 = data.find(_ref15 => {
          let {
            annee
          } = _ref15;
          return annee === year;
        })) === null || _data$find4 === void 0 ? void 0 : _data$find4.valeur) !== null && _data$find$valeur4 !== void 0 ? _data$find$valeur4 : 0;
      }).reduce((a, b) => a + b, 0)
    }));
    chartData.datasets.push({
      borderColor: "#0dcaf0",
      backgroundColor: "#0dcaf0",
      pointStyle: "circle",
      label: "Trajectoire PCAET",
      borderDash: [5, 5],
      pointRadius: 3,
      borderWidth: 2,
      order: -1,
      data: dataSet,
      labels: yearsList
    });
  }
  if (refTrajectories !== null && refTrajectories !== void 0 && refTrajectories.supra_goals) {
    for (const supraGoal of refTrajectories.supra_goals) {
      var _supraGoal$filter;
      if ((_supraGoal$filter = supraGoal.filter) !== null && _supraGoal$filter !== void 0 && _supraGoal$filter.length) continue;
      const dataSet = [{
        x: new Date(supraGoal.annee_reference + "-01-01"),
        y: supraGoal.valeur_reference
      }];
      dataSet.push(...supraGoal.valeurs_annees.map(_ref16 => {
        let {
          annee,
          valeur
        } = _ref16;
        return {
          x: new Date(parseInt(annee, 10) + "-01-01"),
          y: parseFloat(supraGoal.valeur_reference * (1 + valeur / 100))
        };
      }));
      chartData.datasets.push({
        borderColor: supraGoal.couleur,
        backgroundColor: supraGoal.couleur,
        pointStyle: "circle",
        label: supraGoal.titre,
        borderDash: [5, 5],
        pointRadius: 1,
        borderWidth: 2,
        order: -2,
        data: dataSet,
        labels: supraGoal.valeurs_annees.map(_ref17 => {
          let {
            annee
          } = _ref17;
          return annee;
        })
      });
    }
  }
  const chartOptions = {
    scales: {
      x: {
        type: "time",
        title: {
          text: "Années",
          font: {
            size: 16
          }
        },
        time: {
          unit: "year"
        },
        ticks: {
          minRotation: 65,
          source: "data"
        }
      },
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 16
          },
          usePointStyle: true
        },
        reverse: true
      },
      tooltip: {
        mode: "point",
        reverse: true,
        callbacks: {
          title: function (data) {
            return new Date(data[0].label).getFullYear();
          },
          label: function (data) {
            return data.raw.y.toLocaleString(undefined, {
              maximumSignificantDigits: 4
            }) + " " + meta.unit;
          }
        }
      },
      annotation: {
        annotations
      }
    },
    width: 1200,
    height: 600
  };

  // CHART INTERACTIONS
  const trajectoryGraph = useRef(null);
  useEffect(() => {
    var _trajectoryGraph$curr, _trajectoryGraph$curr2;
    const canvas = (_trajectoryGraph$curr = trajectoryGraph.current) === null || _trajectoryGraph$curr === void 0 ? void 0 : (_trajectoryGraph$curr2 = _trajectoryGraph$curr.ctx) === null || _trajectoryGraph$curr2 === void 0 ? void 0 : _trajectoryGraph$curr2.canvas;
    if (!canvas) {
      return;
    }
    let selectedElement, chart, scale, grabOffsetY, newYPosition;
    function selectDataOnTrajectory(event) {
      let elements = trajectoryGraph.current.getElementsAtEventForMode(event, "point", {
        intersect: true
      }, false);
      selectedElement = elements.filter(_ref18 => {
        let {
          datasetIndex
        } = _ref18;
        return datasetIndex === 1;
      })[0];
      if (!selectedElement) return;
      chart = trajectoryGraph.current.config._config;
      chart.options.animation = false;
      scale = trajectoryGraph.current.scales.y;
      // Get pixel y-offset from datapoint to mouse/touch point
      grabOffsetY = scale.getPixelForValue(chart.data.datasets[selectedElement.datasetIndex].data[selectedElement.index].y, selectedElement.index) - event.clientY;
    }
    function moveDataOnTrajectory(event) {
      if (!selectedElement) return;
      const curDataset = chart.data.datasets[selectedElement.datasetIndex];
      const selectedYear = curDataset.data[selectedElement.index].x.getFullYear();

      // On interdit la modification de la première année (valeur de référence)
      if (selectedYear === referenceYear) return;

      /** Lerp value from interval 1 to interval 2 */
      function mapChart(value, start1, stop1, start2, stop2) {
        return start2 + (stop2 - start2) * ((value - start1) / (stop1 - start1));
      }
      newYPosition = mapChart(event.clientY + grabOffsetY, scale.chart.chartArea.bottom, scale.chart.chartArea.top, scale.min, scale.max);
      curDataset.data[selectedElement.index].y = newYPosition;
      scale.chart.update(0);
    }
    function endMoveDataOnTrajectory() {
      if (newYPosition === undefined) {
        selectedElement = undefined;
        return;
      }
      const curDataset = chart.data.datasets[selectedElement.datasetIndex];
      const selectedYear = curDataset.data[selectedElement.index].x.getFullYear();
      const yPositionCopy = newYPosition; // newYPosition is reset just after this. setUserTrajectory being (kind of) async, the order can vary so we need to store a copy

      setUserTrajectory(meta.id, _ref19 => {
        let {
          annees_valeurs,
          annees_modifiees
        } = _ref19;
        return {
          annees_valeurs: {
            ...annees_valeurs,
            [selectedYear]: yPositionCopy
          },
          annees_modifiees: {
            ...annees_modifiees,
            [selectedYear]: true
          }
        };
      });
      chart.options.animation = true;
      newYPosition = undefined;
      selectedElement = undefined;
    }
    canvas.addEventListener("mousedown", selectDataOnTrajectory);
    canvas.addEventListener("mousemove", moveDataOnTrajectory);
    canvas.addEventListener("mouseup", endMoveDataOnTrajectory);
    canvas.addEventListener("mouseout", endMoveDataOnTrajectory);
    return () => {
      canvas.removeEventListener("mousedown", selectDataOnTrajectory);
      canvas.removeEventListener("mousemove", moveDataOnTrajectory);
      canvas.removeEventListener("mouseup", endMoveDataOnTrajectory);
      canvas.removeEventListener("mouseout", endMoveDataOnTrajectory);
    };
  }, [trajectoryGraph, referenceYear, setUserTrajectory, meta.id]);

  // RENDER
  const isLoading = !(userTrajectory !== null && userTrajectory !== void 0 && userTrajectory.annees_valeurs);
  const isEmpty = Object.keys((_userTrajectory$annee2 = userTrajectory === null || userTrajectory === void 0 ? void 0 : userTrajectory.annees_valeurs) !== null && _userTrajectory$annee2 !== void 0 ? _userTrajectory$annee2 : {}).length === 0;
  return /*#__PURE__*/React.createElement("div", {
    className: "trajectory"
  }, isLoading ? /*#__PURE__*/React.createElement("p", null, "Chargement en cours...") : isEmpty ? /*#__PURE__*/React.createElement("p", null, "Aucune donn\xE9e disponible actuellement pour cette trajectoire") : null, !isLoading && !isEmpty && /*#__PURE__*/React.createElement("div", {
    className: "scrollable-grid-container"
  }, /*#__PURE__*/React.createElement(ReactGrid, {
    rows: tableRows,
    columns: tableColumns,
    onCellsChanged: handleTableChange,
    enableFillHandle: true,
    enableRangeSelection: true
  })), /*#__PURE__*/React.createElement(Line, {
    ref: trajectoryGraph,
    hidden: isLoading || isEmpty,
    data: chartData,
    options: chartOptions,
    width: chartOptions.width,
    height: chartOptions.height
  }));
}

/**
 * Interpolate given values depending on a "known" boolean dictionnary.
 *
 * @param {{[key: number]: number}} values Dictionnary of preexisting values depending
 * on the year
 * @param {{[key: number]: boolean}} isKnown Dictionnary containing true for years where
 * values are known and false for years that need to be interpolated
 * @param {boolean} inPlace By default, the function modify the values dict. Set to
 * false to return a copy
 */
function linearInterpolation(values, isKnown) {
  let inPlace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  if (!inPlace) values = {
    ...values
  };
  const allYears = Object.keys(isKnown).map(year => parseInt(year, 10));
  allYears.sort((a, b) => a - b); // Shouldn't be necessary in our use case
  const knownYears = allYears.filter(year => isKnown[year]);
  if (knownYears.length === 0) {
    throw Error(`isKnown should have at least one true value. Got ${JSON.stringify(isKnown)}`);
  }
  for (const year of allYears) {
    if (isKnown[year]) continue;

    // Find where in the known data, the values to interpolate would be inserted
    const prevKnownYear = [...knownYears].reverse().find(y => y < year);
    const nextKnownYear = knownYears.find(y => y >= year);

    // Special cases outside the known region
    if (prevKnownYear === undefined) {
      values[year] = values[nextKnownYear];
      continue;
    }
    if (nextKnownYear === undefined) {
      values[year] = values[prevKnownYear];
      continue;
    }

    // Calculate the slope of the interval in which the current year is
    const prevKnownVal = values[prevKnownYear];
    const nextKnownVal = values[nextKnownYear];
    const slope = (nextKnownVal - prevKnownVal) / (nextKnownYear - prevKnownYear);

    // Calculate the actual value at the current year
    values[year] = slope * (year - prevKnownYear) + prevKnownVal;
  }
  return values;
}